import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { PopOrderAWS } from '../../../@types/AWS';
import { useParams } from 'react-router-dom';
import { NavContext } from '../../../context/NavContext';
import useAuth from '../../../hooks/useAuth';
import axios from '../../../api/axios';
import LoadingIndicator from '../../common/LoadingIndicator/LoadingIndicator';
import { Card, Grid, List, ListItem, ListItemText, Stack } from '@mui/material';
import { SubHeading } from '../../common/PageHeadings/PageHeadings';
import { PopOrderProduct } from '../../../@types/PopOrder';

interface PreviousOrderMerchandiseProps { }

const PreviousOrderMerchandise: FC<PreviousOrderMerchandiseProps> = () => {
   const { user } = useAuth();
   const params = useParams();
   const [loading, setLoading] = useState<boolean>(false);
   const [order, setOrder] = useState<PopOrderAWS>();
   const navContextData = useContext(NavContext);

   const loadOrder = useCallback(async () => {
      try {
         setLoading(true);
         const queryParams = { pk: params.id, dealerId: user?.companyAttributes?.dealerId };
         const response = await axios.get(`/order/pop`, { params: queryParams });
         const order: PopOrderAWS = response.data.data as PopOrderAWS
         setOrder(order);
         navContextData?.setRouteDisplayText(order.po ?? order.dealerId)
      } catch (error) {
         console.log(error);
      } finally {
         setLoading(false);
      }
   }, [setLoading, setOrder, user, params]);

   useEffect(() => {
      loadOrder();
   }, [loadOrder]);

   const USD = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

   return (
      <>
         {!loading
            ? (
               <Grid
                  container
                  spacing={6}
                  sx={(theme) => ({
                     [theme.breakpoints.down('laptopL')]: {
                        flexDirection: 'column-reverse'
                     }
                  })}>
                  <Grid
                     size={{
                        mobileS: 12,
                        mobile: 12,
                        tablet: 12,
                        laptopL: 5,
                        desktop: 5
                     }}>
                     <Stack sx={{ width: '100%' }}>
                        <Stack
                           sx={(theme) => ({
                              marginBottom: '12px',
                              [theme.breakpoints.down('tablet')]: {
                                 textAlign: "center"
                              }
                           })}>
                           <SubHeading>Products</SubHeading>
                        </Stack>
                        <Card>
                           <List
                              sx={{
                                 overflowWrap: 'break-word',
                                 padding: '0px 8px 0px 8px',
                                 '.MuiListItem-root': {
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                    '.MuiListItemText-root': {
                                       lineHeight: '20px',
                                       letterSpacing: '0.1em',
                                       textTransform: 'uppercase',
                                    },
                                    '.MuiListItemText-root:first-of-type': {
                                       marginRight: 12,
                                    },
                                    '.MuiListItemText-root:last-of-type': {
                                       textAlign: 'right',
                                    }
                                 }
                              }}>
                              {order?.products.map((product: PopOrderProduct, index: number) => {
                                 return (
                                    <React.Fragment key={index}>
                                       <ListItem>
                                          <ListItemText>{product.name}</ListItemText>
                                          <ListItemText>{USD.format(product.price)} * {product.quantity} = Total: {USD.format(product.price * product.quantity)}</ListItemText>
                                       </ListItem>
                                    </React.Fragment>
                                 )
                              })}
                              <ListItem>
                                 <ListItemText>Order Total</ListItemText>
                                 <ListItemText>{USD.format(order?.total ?? 0)}</ListItemText>
                              </ListItem>
                           </List>
                        </Card>
                     </Stack>
                  </Grid>
                  <Grid
                     size={{
                        mobileS: 12,
                        mobile: 12,
                        tablet: 12,
                        laptopL: 7,
                        desktop: 7
                     }}>
                     <Stack>
                        <Stack
                           sx={(theme) => ({
                              marginBottom: '12px',
                              [theme.breakpoints.down('tablet')]: {
                                 textAlign: "center"
                              }
                           })}>
                           <SubHeading>Order Details</SubHeading>
                        </Stack>
                        <Card>
                           <List sx={{
                              padding: '0px 8px 0px 8px',
                              '.MuiListItem-root': {
                                 paddingLeft: 0,
                                 paddingRight: 0,
                                 '.MuiListItemText-root': {
                                    lineHeight: '20px',
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                 },
                                 '.MuiListItemText-root:first-of-type': {
                                    marginRight: 12,
                                 },
                                 '.MuiListItemText-root:last-of-type': {
                                    textAlign: 'right',
                                 }
                              }
                           }}>
                              <ListItem>
                                 <ListItemText>PO</ListItemText>
                                 <ListItemText>{order?.po}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>Date Created</ListItemText>
                                 <ListItemText>{order?.dateCreated}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>Email</ListItemText>
                                 <ListItemText>{order?.email}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>Street Address</ListItemText>
                                 <ListItemText>{order?.shippingAddress.street}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>Address Line 2</ListItemText>
                                 <ListItemText>{order?.shippingAddress.street2}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>City</ListItemText>
                                 <ListItemText>{order?.shippingAddress.city}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>State</ListItemText>
                                 <ListItemText>{order?.shippingAddress.state}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>Zip</ListItemText>
                                 <ListItemText>{order?.shippingAddress.zip}</ListItemText>
                              </ListItem>
                              <ListItem>
                                 <ListItemText>Country</ListItemText>
                                 <ListItemText>{order?.shippingAddress.country}</ListItemText>
                              </ListItem>
                           </List>
                        </Card>
                     </Stack>
                  </Grid>
               </Grid>
            )
            : (
               <LoadingIndicator loading={loading} />
            )
         }
      </>
   );
}

export default PreviousOrderMerchandise;
